import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import { getErrorMessage } from '@/utils/errorUtils';
import { pushError } from '@/utils/recentErrors';

type ErrorListener = (message: string) => void;

let errorListener: ErrorListener | null = null;

export function setErrorListener(listener: ErrorListener | null) {
  errorListener = listener;
}

/**
 * RTK Query middleware that intercepts rejected API calls and
 * notifies the global error listener (toast provider).
 * Skips 401 (handled by reauth) and 404 (often expected for queries).
 */
export const rtkQueryErrorMiddleware: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const payload = action.payload as { status?: number; data?: unknown } | undefined;

    // Skip auth errors (handled by baseQueryWithReauth) and 404s (often expected)
    const status = payload?.status;

    // Record for support reports (include the endpoint when available).
    const endpoint = (action.meta as { arg?: { endpointName?: string } } | undefined)?.arg?.endpointName;
    pushError(`API ${status ?? 'error'} ${endpoint ?? ''}`.trim());

    if (status === 401 || status === 404) {
      return next(action);
    }

    const message = getErrorMessage(payload);
    errorListener?.(message);
  }

  return next(action);
};
